import { DateTime } from 'luxon';
import * as buttons from 'smg/buttons';
import { OpenQuotes } from 'smg/customer-api/v2/quotes';
import { GetLineItems } from 'smg/customer-api/lineitems';
import * as models from 'smg/customer-api/models';
import { HumanFileSize } from 'smg/formatting';
import * as html from 'smg/html';
import { Linkify } from 'smg/linkify';
import { IsImageURL, Picture } from 'smg/picture';
import { GetProofURLs } from 'smg/proofs';
import QueryString from 'smg/querystring';
import { SanitizeRichHTML } from 'smg/richtext';
import * as template from 'smg/template';
import * as carts from '../carts';
import { Relref } from '../hugo';
import * as lineitems from '../lineitems';
import { SetupRevisionLink } from '../quotes';

declare interface myWindow extends Window {
    readonly cloudFront: string;
    readonly apiRoot: string;
}
declare const window: myWindow;

main();

function main() {
    const container = document.querySelector('#quotes-container') as HTMLElement;
    const spinner = document.querySelector('#quotes-spinner') as HTMLElement;
    const searchParent = document.querySelector('#search-parent') as HTMLElement;
    const searchEl = document.querySelector('#quote-search') as HTMLInputElement;

    const currQuery = QueryString().get('q');
    let q: string = currQuery || '';
    const quoteID = QueryString().get('id');
    const quoteSentInstanceID = QueryString().get('instance');

    const showSearch = () => {
        if (currQuery) {
            searchEl.value = currQuery;
        }

        searchParent.classList.remove('d-none');
        spinner.parentElement?.removeChild(spinner);
    };

    if (quoteID) {
        if (quoteID.length === 43) { // key
            q = `key:${quoteID}`;
        } else if (quoteID.length === 38) { // sha3224
            q = `sha3224:${quoteID}`;
        } else {
            q = `id:${quoteID}`;
        }
    } else if (quoteSentInstanceID) {
        if (quoteSentInstanceID.length === 43) { // key
            q = `quoteSentKey:${quoteSentInstanceID}`;
        } else {
            q = `quoteSentInstanceID:${quoteSentInstanceID}`;
        }
    } else if (!q) {
        showSearch();
        return;
    }

    const currPage = 0;

    OpenQuotes({
        sort: 'created asc',
        max: 100,
        data: 'serial,created,quoteLineItems,quoteProofs,cachedTotal,quoteOrders,quoteRequest,key,quoteSents',
        p: currPage,
        q,
    }).then(async data => {
        if (!data.results.length) {
            if (currQuery) {
                const emptySearchWarning = document.querySelector('#empty-search-warning') as HTMLElement;
                emptySearchWarning.classList.remove('d-none');
            }

            showSearch();
            return;
        }

        const backToSearch = document.querySelector('#back-to-search') as HTMLElement;
        if (!currQuery) {
            backToSearch.parentElement?.removeChild(backToSearch);
        }

        // Group quotes by the quote-sent instance of each quote's most recent
        // quote-sent: quotes sent together share an instance, so each group
        // gets its own message card above its quotes. Quotes that were never
        // sent render after every group, without a message card. Most sends
        // are SMS-only, so the message the rep wrote never reaches the
        // customer anywhere else — surface it once per group.
        interface quoteGroup {
            quoteSent: models.QuoteSent;
            quoteSentMs: number;
            quotes: models.Quote[];
        }

        const groupsByKey = new Map<string, quoteGroup>();
        const unsentQuotes: models.Quote[] = [];

        for (const quote of data.results) {
            let latestQuoteSent: models.QuoteSent | undefined;
            let latestQuoteSentMs = -1;
            for (const quoteSent of quote.quoteSents) {
                const ms = quoteSent.created?.isValid ? quoteSent.created.toMillis() : 0;
                if (ms > latestQuoteSentMs) {
                    latestQuoteSentMs = ms;
                    latestQuoteSent = quoteSent;
                }
            }

            if (!latestQuoteSent) {
                unsentQuotes.push(quote);
                continue;
            }

            const key = latestQuoteSent.quoteSentInstanceID || latestQuoteSent.id || '';
            const group = groupsByKey.get(key);
            if (group) {
                group.quotes.push(quote);
                if (latestQuoteSentMs > group.quoteSentMs) {
                    group.quoteSentMs = latestQuoteSentMs;
                    group.quoteSent = latestQuoteSent;
                }
            } else {
                groupsByKey.set(key, { quoteSent: latestQuoteSent, quoteSentMs: latestQuoteSentMs, quotes: [quote] });
            }
        }

        const groups = [...groupsByKey.values()];
        groups.sort((a, b) => b.quoteSentMs - a.quoteSentMs);

        const renderQuoteSentCard = (quoteSent: models.QuoteSent, older: boolean) => {
            const quoteSentContent = quoteSent.content?.trim() ?? '';
            const subject = quoteSent.subject?.trim() ?? '';
            const attachments = quoteSent.attachments.filter(a => a.publicURL);
            // Subject-only sends still render a header card — the subject often
            // carries the send's intent (e.g. preliminary vs action needed).
            if (!quoteSentContent && !subject && !attachments.length) {
                return undefined;
            }

            const el = template.Clone('#quote-sent-template');

            if (older) {
                const $card = el.firstElementChild as HTMLElement;
                $card.classList.remove('shadow-sm');
                $card.classList.add('quote-sent-card-older');
            }

            const firstName = quoteSent.user?.firstName?.trim() ?? '';
            (el.querySelector('.quote-sent-from') as HTMLElement).textContent = firstName ?
                `A message from ${firstName}` :
                'A message from our team';

            (el.querySelector('.quote-sent-title') as HTMLElement).textContent = subject || 'Your Quote Is Ready';

            const $date = el.querySelector('.quote-sent-date') as HTMLElement;
            if (quoteSent.created?.isValid) {
                $date.textContent = quoteSent.created.toLocaleString(DateTime.DATETIME_MED);
            } else {
                $date.remove();
            }

            const $body = el.querySelector('.quote-sent-body') as HTMLElement;
            if (quoteSentContent) {
                if (quoteSent.contentType === 'text/html') {
                    $body.innerHTML = Linkify(SanitizeRichHTML(quoteSentContent));
                } else {
                    $body.innerHTML = Linkify(html.Nl2br(html.Escape(quoteSentContent)));
                }
            } else {
                $body.remove();
            }

            // Files attached to the send's email (reference images, proof
            // JPGs, PDF guides) render below the body: images as thumbnails
            // that open in a per-send fancybox gallery, everything else as a
            // file chip opening in a new tab. A send with attachments but no
            // message body still gets its card — the attachments are content
            // worth showing on their own.
            const $attachments = el.querySelector('.quote-sent-attachments') as HTMLElement;
            if (attachments.length) {
                const gallery = `quote-sent-attachments-${quoteSent.quoteSentInstanceID || quoteSent.id || ''}`;
                for (const attachment of attachments) {
                    const name = attachment.name || 'Attachment';

                    if (IsImageURL(attachment.name || attachment.key)) {
                        const $parent = template.Clone('#quote-sent-attachment-image-template').firstElementChild as HTMLElement;

                        const $link = $parent.querySelector('a') as HTMLAnchorElement;
                        $link.href = attachment.publicURL;
                        $link.title = name;
                        // Picture's fancyboxThumb lookup runs synchronously, so
                        // data-fancybox must be on the anchor before the call.
                        $link.setAttribute('data-fancybox', gallery);
                        $link.setAttribute('data-caption', name);

                        const $img = $link.querySelector('img') as HTMLImageElement;
                        $img.alt = name;

                        void Picture($img, attachment.publicURL, {
                            displayWidth: 64, // 4rem thumbnail (see quote.scss)
                            fancyboxThumb: true,
                        });

                        $attachments.appendChild($parent);
                    } else {
                        const $parent = template.Clone('#quote-sent-attachment-file-template').firstElementChild as HTMLElement;

                        const $link = $parent.querySelector('a') as HTMLAnchorElement;
                        $link.href = attachment.publicURL;
                        $link.title = name;

                        ($link.querySelector('.quote-sent-attachment-file-name') as HTMLElement).textContent = name;

                        const $size = $link.querySelector('.quote-sent-attachment-file-size') as HTMLElement;
                        if (attachment.size) {
                            $size.textContent = HumanFileSize(attachment.size);
                        } else {
                            $size.remove();
                        }

                        $attachments.appendChild($parent);
                    }
                }
            } else {
                $attachments.remove();
            }

            return el;
        };

        const renderQuoteCard = async (quote: models.Quote) => {
            const el = template.Clone('#quote-template');

            const serial = el.querySelector('.quote-serial') as HTMLElement;
            serial.textContent = String(quote.serial);

            const summary = el.querySelector('.quote-summary') as HTMLElement;
            summary.appendChild(await lineitems.Table(quote.quoteLineItems));

            const proofsCount = quote.quoteProofs.length;

            const proofsContainer = el.querySelector('.quote-proofs-container') as HTMLElement;
            const revisionLink = el.querySelector('.quote-request-revision') as HTMLAnchorElement;
            const proofsParent = el.querySelector('.quote-proofs-parent') as HTMLElement;
            if (quote.quoteProofs.length) {
                for (const op of quote.quoteProofs) {
                    const p = op.proof;
                    if (!p) {
                        continue;
                    }

                    const urls = GetProofURLs(window.cloudFront, p.file || '');

                    const el = template.Clone('#quote-proof-template').firstElementChild as HTMLElement;

                    const a = el.querySelector('a') as HTMLAnchorElement;
                    a.href = urls.main;
                    a.setAttribute('data-fancybox', quote.id || '');

                    const img = a.querySelector('img') as HTMLImageElement;
                    img.src = urls.main;
                    img.alt = op.name || '';

                    const $proofName = el.querySelector('.proof-name') as HTMLElement | null;
                    if (op.name) {
                        if ($proofName) {
                            $proofName.textContent = op.name;
                            $proofName.title = op.name;
                        }
                    } else {
                        $proofName?.remove();
                    }

                    proofsParent.appendChild(el);
                }

                const revBase = `${Relref('/revisions')}?id=${quoteID || quote.key}`;
                SetupRevisionLink(revisionLink, quote.quoteProofs, window.cloudFront, revBase);
            }

            const addToCart = el.querySelector('.quote-add-to-cart') as HTMLButtonElement;
            addToCart.addEventListener('click', () => {
                buttons.Start(addToCart);
                void carts.AddCartItem({ quoteID: quote.key }, {
                    promptToCheckout: data.results.length !== 1,
                }).then(() => {
                    addToCart.classList.remove('btn-primary', 'btn-success', 'text-white');
                    addToCart.classList.add('btn-outline-success');

                    const $icon = addToCart.querySelector('.material-symbols-sharp');
                    if ($icon) {
                        $icon.textContent = 'check';
                    }
                }).finally(() => {
                    buttons.Stop(addToCart);
                    if (data.results.length === 1) {
                        window.location.href = Relref('/cart');
                    }
                });
            });

            const cardBody = el.querySelector('.card-body') as HTMLElement;

            if (proofsCount) {
                cardBody.classList.add('row-cols-lg-2');

                if (proofsCount > 1) {
                    proofsParent.classList.add('row-cols-lg-2');
                }
            } else {
                proofsContainer.parentElement?.removeChild(proofsContainer);
                revisionLink.parentElement?.removeChild(revisionLink);
            }

            return el;
        };

        // Flanked-line divider carrying a section label, rendered at every
        // group boundary so each quote-sent instance reads as its own
        // section — even when the group's message card was skipped for
        // empty content. The first older group's divider doubles as the
        // "Earlier Quotes" transition from the latest send; later ones just
        // carry their send date, and the never-sent tail (when it follows
        // sent groups) closes the page with a plain label.
        const renderGroupDivider = (label: string) => {
            const el = template.Clone('#quote-group-divider-template');
            (el.querySelector('.quote-group-divider-label') as HTMLElement).textContent = label;
            return el;
        };

        const f = document.createDocumentFragment();

        for (const [i, group] of groups.entries()) {
            if (i > 0) {
                const date = group.quoteSent.created?.isValid ?
                    group.quoteSent.created.toLocaleString(DateTime.DATE_MED) : '';

                let label: string;
                if (i === 1) {
                    label = date ? `Earlier Quotes · ${date}` : 'Earlier Quotes';
                } else {
                    label = date ? `Sent ${date}` : 'Sent earlier';
                }

                f.appendChild(renderGroupDivider(label));
            }

            const $sentCard = renderQuoteSentCard(group.quoteSent, i > 0);
            if ($sentCard) {
                f.appendChild($sentCard);
            }

            for (const quote of group.quotes) {
                f.appendChild(await renderQuoteCard(quote));
            }
        }

        if (groups.length && unsentQuotes.length) {
            f.appendChild(renderGroupDivider('Additional Quotes'));
        }

        for (const quote of unsentQuotes) {
            f.appendChild(await renderQuoteCard(quote));
        }

        spinner.parentElement?.removeChild(spinner);
        container.appendChild(f);
        container.classList.remove('d-none');
    }).catch(err => {
        showSearch();
        throw err;
    });

    void GetLineItems();
}